<div *transloco="let t" class="flex flex-col flex-auto min-w-0">
    <div class="flex-auto mb-4">
        <div class="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4 py-4 px-5 md:px-10">
            <div>
                <h1 class="text-2xl md:text-3xl font-extrabold tracking-tight leading-none">{{ t('Reputation Dashboard') }}</h1>
                <p class="mt-1.5 text-sm text-secondary">{{ t('Track your review campaigns and customer feedback performance') }}</p>
            </div>
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                <div class="relative inline-flex">
                    <button mat-stroked-button [matMenuTriggerFor]="dateMenu"
                            class="h-10 px-3 rounded-xl border-gray-200 bg-white min-w-40"
                            [class.pr-9]="dateFilter !== 'last-10-days'">
                        <span class="flex items-center gap-2 text-sm font-medium">
                            <mat-icon class="icon-size-4 text-secondary" [svgIcon]="'heroicons_outline:calendar'"></mat-icon>
                            {{ t(dateFilterLabel) }}
                        </span>
                        <mat-icon class="icon-size-4 text-secondary shrink-0 ml-auto"
                                  *ngIf="dateFilter === 'last-10-days'"
                                  [svgIcon]="'heroicons_outline:chevron-down'"></mat-icon>
                    </button>
                    <button *ngIf="dateFilter !== 'last-10-days'"
                            type="button"
                            class="absolute right-1.5 top-1/2 z-10 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full text-secondary hover:bg-gray-100 hover:text-default"
                            [matTooltip]="t('Clear filter')"
                            (click)="clearDateFilter($event)">
                        <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:x'"></mat-icon>
                    </button>
                    <mat-menu #dateMenu="matMenu">
                        <button mat-menu-item (click)="setDateFilter('last-10-days')">{{ t('Last 10 Days') }}</button>
                        <button mat-menu-item (click)="setDateFilter('last-30-days')">{{ t('Last 30 Days') }}</button>
                        <button mat-menu-item (click)="setDateFilter('this-month')">{{ t('This Month') }}</button>
                        <button mat-menu-item (click)="setDateFilter('last-month')">{{ t('Last Month') }}</button>
                        <button mat-menu-item (click)="setDateFilter('custom')">{{ t('Custom') }}</button>
                    </mat-menu>
                </div>
                <div *ngIf="dateFilter === 'custom'" class="flex items-center">
                    <mat-form-field appearance="outline" class="w-full sm:w-56 fuse-mat-dense mb-0">
                        <mat-date-range-input [formGroup]="range" [rangePicker]="picker">
                            <input matStartDate formControlName="startDate" [placeholder]="t('Start Date')">
                            <input matEndDate formControlName="endDate" [placeholder]="t('End Date')">
                        </mat-date-range-input>
                        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                        <mat-date-range-picker #picker></mat-date-range-picker>
                    </mat-form-field>
                </div>
                <div class="flex items-center gap-2">
                    <button mat-stroked-button class="h-10 rounded-xl" [matTooltipPosition]="'above'" (click)="editLocation()"
                            [matTooltip]="t('Modify Location Detail')">
                        <mat-icon class="icon-size-4 rotate-90" [svgIcon]="'heroicons_outline:adjustments'"></mat-icon>
                        <span class="ml-2 text-sm">{{ t('Location Settings') }}</span>
                    </button>
                    <button [disabled]="!orgSubscription || !orgSubscription.active"
                            color="primary" class="fuse-mat-button-rounded text-white h-10 rounded-xl" mat-flat-button
                            [routerLink]="enrollRoute">
                        <mat-icon [svgIcon]="'heroicons_outline:user-add'" class="icon-size-4"></mat-icon>
                        <span class="ml-2 text-sm">{{ t(enrollLabelKey) }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div *ngIf="isLoading" class="flex justify-center py-10">
            <mat-spinner diameter="36"></mat-spinner>
        </div>

        <div [class.opacity-60]="isRefreshing" [class.pointer-events-none]="isRefreshing">
            <ng-container *ngIf="!isLoading">
                <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 px-5 md:px-10 mb-4">
                    <div *ngFor="let card of kpiCards; let i = index"
                         class="bg-card rounded-2xl border border-gray-100 p-4 shadow-sm flex flex-col">
                        <div class="flex items-center gap-2 min-w-0">
                            <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" [ngClass]="card.iconClass">
                                <mat-icon class="icon-size-4" [svgIcon]="card.icon"></mat-icon>
                            </div>
                            <div class="flex items-center gap-1 min-w-0 text-xs text-secondary">
                                <span class="truncate">{{ t(card.title) }}</span>
                                <mat-icon class="icon-size-3 shrink-0 cursor-help"
                                          [matTooltip]="t(card.tooltip)"
                                          [svgIcon]="'heroicons_outline:information-circle'"></mat-icon>
                            </div>
                        </div>
                        <div class="mt-2 mb-3">
                            <div class="text-2xl font-bold leading-none">
                                <ng-container *ngIf="!showKpiRating(i)">{{ getKpiValue(i) }}</ng-container>
                                <ng-container *ngIf="showKpiRating(i)">{{ getKpiValue(i) | number:'0.0-1' }}</ng-container>
                            </div>
                            <rating *ngIf="showKpiRating(i)" [rating]="kpis.googleRating" [size]="3" class="mt-1 block"></rating>
                        </div>
                        <div class="mt-3 pt-3 border-t border-gray-100 mt-auto flex flex-wrap gap-1.5">
                            <span *ngIf="getKpiDelta(i)"
                                  class="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700 border border-green-100">
                                {{ getKpiDelta(i) }} {{ t(getKpiDeltaLabel(i)) }}
                            </span>
                            <span *ngIf="showResponseRatePill(i)"
                                  class="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700 border border-green-100">
                                {{ t('Response Rate') }} {{ kpis.responseRate }}%
                            </span>
                            <span *ngIf="showConversionRatePill(i)"
                                  class="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                                {{ t('Conversion Rate') }} {{ kpis.conversionRate }}%
                            </span>
                            <span *ngIf="i === 4" class="text-[11px] text-secondary leading-snug">
                                {{ t('Based on') }} {{ totalGoogleReviews }} {{ t('reviews') }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 px-5 md:px-10 mb-4">
                    <div class="xl:col-span-2 flex flex-col gap-4 min-w-0">
                    <div class="bg-card rounded-2xl border border-gray-100 p-4 md:p-5 shadow-sm">
                        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
                            <div>
                                <h2 class="text-lg font-bold">{{ t('Campaign Performance') }}</h2>
                                <p class="text-xs text-secondary mt-0.5">{{ fromDateLabel }} – {{ toDateLabel }}</p>
                            </div>
                            <div class="flex flex-wrap items-center gap-2">
                                <div class="relative inline-flex">
                                    <button mat-stroked-button [matMenuTriggerFor]="campaignMenu"
                                            class="h-9 px-3 rounded-xl border-gray-200 bg-white min-w-36"
                                            [class.pr-9]="campaignFilter !== 'all'">
                                        <span class="text-sm font-medium truncate">{{ t(campaignFilterLabel) }}</span>
                                        <mat-icon class="icon-size-4 text-secondary shrink-0 ml-auto"
                                                  *ngIf="campaignFilter === 'all'"
                                                  [svgIcon]="'heroicons_outline:chevron-down'"></mat-icon>
                                    </button>
                                    <button *ngIf="campaignFilter !== 'all'"
                                            type="button"
                                            class="absolute right-1.5 top-1/2 z-10 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-secondary hover:bg-gray-100 hover:text-default"
                                            [matTooltip]="t('Clear filter')"
                                            (click)="clearCampaignFilter($event)">
                                        <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:x'"></mat-icon>
                                    </button>
                                    <mat-menu #campaignMenu="matMenu">
                                        <button mat-menu-item (click)="setCampaignFilter('all')">{{ t('All Campaigns') }}</button>
                                        <button mat-menu-item (click)="setCampaignFilter('review-campaigns')">{{ t('Review Campaigns') }}</button>
                                        <button mat-menu-item (click)="setCampaignFilter('google-reviews')">{{ t('Google Reviews') }}</button>
                                        <button mat-menu-item (click)="setCampaignFilter('feedback-responses')">{{ t('Feedback Responses') }}</button>
                                    </mat-menu>
                                </div>
                                <div class="relative inline-flex">
                                    <button mat-stroked-button [matMenuTriggerFor]="granularityMenu"
                                            class="h-9 px-3 rounded-xl border-gray-200 bg-white min-w-28"
                                            [class.pr-9]="chartGranularity !== 'daily'">
                                        <span class="text-sm font-medium">{{ t(chartGranularityLabel) }}</span>
                                        <mat-icon class="icon-size-4 text-secondary shrink-0 ml-auto"
                                                  *ngIf="chartGranularity === 'daily'"
                                                  [svgIcon]="'heroicons_outline:chevron-down'"></mat-icon>
                                    </button>
                                    <button *ngIf="chartGranularity !== 'daily'"
                                            type="button"
                                            class="absolute right-1.5 top-1/2 z-10 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-secondary hover:bg-gray-100 hover:text-default"
                                            [matTooltip]="t('Clear filter')"
                                            (click)="clearChartGranularity($event)">
                                        <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:x'"></mat-icon>
                                    </button>
                                    <mat-menu #granularityMenu="matMenu">
                                        <button mat-menu-item (click)="setChartGranularity('daily')">{{ t('Daily') }}</button>
                                        <button mat-menu-item (click)="setChartGranularity('weekly')">{{ t('Weekly') }}</button>
                                        <button mat-menu-item (click)="setChartGranularity('monthly')">{{ t('Monthly') }}</button>
                                    </mat-menu>
                                </div>
                            </div>
                        </div>
                        <apx-chart *ngIf="!isMobileDevice" #chart
                                   [chart]="chartOptions?.chart"
                                   [colors]="chartOptions?.colors"
                                   [series]="chartOptions?.series"
                                   [tooltip]="chartOptions?.tooltip"
                                   [xaxis]="chartOptions?.xaxis"
                                   [yaxis]="chartOptions?.yaxis"
                                   [plotOptions]="chartOptions?.plotOptions"
                                   [legend]="chartOptions?.legend"
                                   [grid]="chartOptions?.grid"
                                   class="w-full">
                        </apx-chart>
                        <div *ngIf="isMobileDevice" class="text-sm text-secondary py-6 text-center">
                            {{ t('Open on desktop to view campaign performance chart') }}
                        </div>
                        <div class="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
                            <div><span class="text-secondary">{{ t('Enrollments') }}</span><div class="font-bold text-base">{{ chartTotals.enrollments }}</div></div>
                            <div><span class="text-secondary">{{ t('Responses') }}</span><div class="font-bold text-base">{{ chartTotals.responses }}</div></div>
                            <div><span class="text-secondary">{{ t('Response Rate') }}</span><div class="font-bold text-base">{{ chartTotals.responseRate }}%</div></div>
                            <div><span class="text-secondary">{{ t('Google Reviews') }}</span><div class="font-bold text-base">{{ chartTotals.googleReviews }}</div></div>
                            <div><span class="text-secondary">{{ t('Conversion Rate') }}</span><div class="font-bold text-base">{{ chartTotals.conversionRate }}%</div></div>
                        </div>
                    </div>

                    <div class="bg-card rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div class="flex items-center px-4 md:px-5 py-3 border-b border-gray-100">
                            <h2 class="text-lg font-bold">{{ t('Recent Feedback') }}</h2>
                            <button mat-button color="primary" class="min-w-0 px-2" (click)="goToReviewAnalytics()">
                                {{ t('View all') }}
                                <mat-icon class="icon-size-4 ml-1" [svgIcon]="'heroicons_outline:arrow-right'"></mat-icon>
                            </button>
                        </div>
                        <div class="overflow-x-auto">
                            <table [dataSource]="recentFeedback" class="w-full reputation-feedback-table" mat-table>
                                <ng-container matColumnDef="customer">
                                    <th *matHeaderCellDef class="font-semibold uppercase text-[11px] tracking-wide text-secondary py-2" mat-header-cell>{{ t('Customer') }}</th>
                                    <td *matCellDef="let row" class="py-2.5" mat-cell>
                                        <div class="flex items-center gap-2.5">
                                            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                                                {{ row.initials }}
                                            </div>
                                            <div class="font-medium text-sm">{{ row.customerName }}</div>
                                        </div>
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="sequence">
                                    <th *matHeaderCellDef class="font-semibold uppercase text-[11px] tracking-wide text-secondary py-2" mat-header-cell>{{ t('Sequence') }}</th>
                                    <td *matCellDef="let row" class="text-secondary text-sm py-2.5" mat-cell>{{ row.subtitle }}</td>
                                </ng-container>
                                <ng-container matColumnDef="rating">
                                    <th *matHeaderCellDef class="font-semibold uppercase text-[11px] tracking-wide text-secondary py-2" mat-header-cell>{{ t('Rating') }}</th>
                                    <td *matCellDef="let row" class="py-2.5" mat-cell>
                                        <span class="text-sm" [ngClass]="ratingClass(row.rating, row.ratingScale)">
                                            {{ row.rating != null ? (row.rating + '/' + row.ratingScale) : '—' }}
                                        </span>
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="status">
                                    <th *matHeaderCellDef class="font-semibold uppercase text-[11px] tracking-wide text-secondary py-2" mat-header-cell>{{ t('Status') }}</th>
                                    <td *matCellDef="let row" class="py-2.5" mat-cell>
                                        <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold" [ngClass]="row.statusClass">{{ row.status }}</span>
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="time">
                                    <th *matHeaderCellDef class="font-semibold uppercase text-[11px] tracking-wide text-secondary py-2" mat-header-cell>{{ t('Time') }}</th>
                                    <td *matCellDef="let row" class="text-secondary text-sm whitespace-nowrap py-2.5" mat-cell>
                                        {{ row.time ? (row.time | timeago) : '—' }}
                                    </td>
                                </ng-container>
                                <tr *matHeaderRowDef="recentFeedbackColumns" mat-header-row></tr>
                                <tr *matRowDef="let row; columns: recentFeedbackColumns" mat-row></tr>
                            </table>
                            <div *ngIf="!recentFeedback.length" class="px-5 py-8 text-center text-secondary text-sm">
                                {{ t('No recent feedback in this date range') }}
                            </div>
                        </div>
                    </div>
                    </div>

                    <div class="flex flex-col gap-4 min-w-0">
                    <div class="bg-card rounded-2xl border border-gray-100 p-4 shadow-sm w-full">
                        <div class="flex items-center justify-between gap-2">
                            <div class="flex items-center gap-2 min-w-0">
                                <h2 class="text-lg font-bold">{{ t('Review Summary') }}</h2>
                                <mat-icon class="icon-size-4 text-secondary shrink-0" [svgIcon]="'heroicons_outline:information-circle'"></mat-icon>
                            </div>
                            <button mat-button color="primary" class="px-0 min-w-0 shrink-0" (click)="goToReviewAnalytics()">
                                <span class="text-sm">{{ t('View all') }}</span>
                                <mat-icon class="icon-size-4 ml-1" [svgIcon]="'heroicons_outline:arrow-right'"></mat-icon>
                            </button>
                        </div>
                        <div class="mt-3 space-y-2.5">
                            <div class="rounded-xl bg-gray-50 px-3 py-2.5">
                                <div class="text-[11px] font-semibold text-secondary uppercase tracking-wide">{{ t('Customer Feedback (Private)') }}</div>
                                <div class="mt-2 grid grid-cols-3 gap-2">
                                    <div>
                                        <div class="text-[11px] text-secondary leading-tight">{{ t('CSAT') }}</div>
                                        <div class="font-bold text-base text-green-700 leading-snug">{{ csatSurveyCategory?.average | number:'0.0-1' }}/5</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-secondary leading-tight">{{ t('NPS') }}</div>
                                        <div class="font-bold text-base leading-snug"
                                             [ngClass]="(npsSurveyCategory?.average ?? 0) >= 0 ? 'text-green-700' : 'text-red-500'">
                                            {{ npsSurveyCategory?.average | number:'0.0-1' }}
                                        </div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-secondary leading-tight">{{ t('Responses') }}</div>
                                        <div class="font-bold text-base leading-snug">{{ kpis.responsesReceived }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="rounded-xl bg-gray-50 px-3 py-2.5">
                                <div class="text-[11px] font-semibold text-secondary uppercase tracking-wide">{{ t('Google Reviews (Public)') }}</div>
                                <div class="mt-2 grid grid-cols-3 gap-2">
                                    <div>
                                        <div class="text-[11px] text-secondary leading-tight">{{ t('Total') }}</div>
                                        <div class="font-bold text-base leading-snug">{{ totalGoogleReviews }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-secondary leading-tight">{{ t('This Month') }}</div>
                                        <div class="font-bold text-base leading-snug">{{ googleReviewsThisMonth }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] text-secondary leading-tight">{{ t('Avg Rating') }}</div>
                                        <div class="font-bold text-base text-amber-600 leading-snug">{{ kpis.googleRating | number:'0.0-1' }}/5</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="bg-card rounded-2xl border border-gray-100 p-4 md:p-5 shadow-sm">
                        <div class="flex items-center mb-3">
                            <h2 class="text-lg font-bold">{{ t('Needs Attention') }}</h2>
                            <button mat-button color="primary" class="min-w-0 px-2" routerLink="/analytics/review">
                                {{ t('View all') }}
                                <mat-icon class="icon-size-4 ml-1" [svgIcon]="'heroicons_outline:arrow-right'"></mat-icon>
                            </button>
                        </div>
                        <div class="space-y-2">
                            <button *ngFor="let alert of alerts"
                                    class="w-full flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2.5 text-left hover:bg-gray-50 transition"
                                    [routerLink]="alert.route">
                                <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" [ngClass]="alertTileClass(alert.severity)">
                                    <mat-icon class="icon-size-4" [svgIcon]="alert.icon"></mat-icon>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-sm font-semibold leading-tight">{{ t(alert.label) }}</div>
                                    <div class="text-[11px] text-secondary mt-0.5">{{ t(alert.description) }}</div>
                                </div>
                                <div class="flex items-center gap-2 shrink-0">
                                    <span class="inline-flex min-w-7 justify-center rounded-full px-2 py-0.5 text-xs font-bold"
                                          [ngClass]="{
                                            'bg-red-100 text-red-700': alert.severity === 'red',
                                            'bg-amber-100 text-amber-700': alert.severity === 'orange',
                                            'bg-blue-100 text-blue-700': alert.severity === 'blue'
                                          }">
                                        {{ alert.count }}
                                    </span>
                                    <mat-icon class="icon-size-4 text-secondary" [svgIcon]="'heroicons_outline:chevron-right'"></mat-icon>
                                </div>
                            </button>
                            <div *ngIf="!alerts.length" class="text-sm text-secondary py-6 text-center">
                                {{ t('No items need attention right now') }}
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </ng-container>
        </div>
    </div>
</div>
